import {
	ColDef,
	GetRowIdParams,
	SizeColumnsToContentStrategy,
	ValueGetterParams,
} from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import type { TFunction } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { CycleEmployee } from '@treams/treams-analytics';
import { DataGrid, Pill } from '@treams/treams-ui';

import { CycleColumn, EmployeeRow, buildEmployeeColumns, buildEmployeeRows } from './cycleColumns';

const autoSizeStrategy: SizeColumnsToContentStrategy = {
	type: 'fitCellContents',
	colIds: ['departments'],
};

const defaultColDef: ColDef<EmployeeRow> = {
	sortingOrder: ['asc', 'desc'],
};

const gridColumnLayout: Record<string, ColDef<EmployeeRow>> = {
	employee: { initialSort: 'asc', flex: 2, minWidth: 200 },
	status: { flex: 1, minWidth: 180 },
	manager: { flex: 2, minWidth: 180 },
	departments: { minWidth: 200, flex: 0 },
};

function statusTint(row: EmployeeRow): string {
	if (row.status === 'notStarted') {
		return 'slateBlue';
	}

	return row.done ? 'green' : 'blue';
}

function StatusCell({ data }: CustomCellRendererProps<EmployeeRow>) {
	const { t } = useTranslation();

	if (!data) {
		return null;
	}

	return (
		<Flex alignItems="center" height="100%">
			<Pill small tint={statusTint(data)}>
				{t(`analytics.cycles.managers.table.${data.status}`)}
			</Pill>
		</Flex>
	);
}

function buildEmployeeGridColumnDefs(t: TFunction): ColDef<EmployeeRow>[] {
	return buildEmployeeColumns(t).map(toColDef);
}

function toColDef(column: CycleColumn<EmployeeRow>): ColDef<EmployeeRow> {
	const sortValue = column.type === 'text' ? column.sortValue : undefined;

	return {
		colId: column.id,
		headerName: column.header,
		valueGetter: ({ data }: ValueGetterParams<EmployeeRow>) =>
			data ? (sortValue ? sortValue(data) : column.value(data)) : '',
		cellRenderer:
			column.id === 'status'
				? StatusCell
				: ({ data }: CustomCellRendererProps<EmployeeRow>) => (data ? column.value(data) : null),
		...gridColumnLayout[column.id],
	};
}

export function CycleEmployeesGrid({
	employees,
	activeGroupIDs,
	momentID,
}: {
	employees: CycleEmployee[];
	activeGroupIDs: Set<string>;
	momentID: string | undefined;
}) {
	const { t } = useTranslation();

	const rowData = useMemo(
		() => buildEmployeeRows(employees, momentID, activeGroupIDs),
		[employees, momentID, activeGroupIDs],
	);

	const columnDefs = useMemo<ColDef<EmployeeRow>[]>(() => buildEmployeeGridColumnDefs(t), [t]);

	const getRowId = useCallback(({ data }: GetRowIdParams<EmployeeRow>) => data?.user.id ?? '', []);

	return (
		<DataGrid<EmployeeRow>
			key={momentID}
			rowData={rowData}
			columnDefs={columnDefs}
			defaultColDef={defaultColDef}
			autoSizeStrategy={autoSizeStrategy}
			getRowId={getRowId}
		/>
	);
}
